import { ContactHelpLink } from '@/components/ContactHelpLink';
import { SplitAuthShell } from '@/components/SplitAuthShell';
import { Flex } from '@chakra-ui/react';
import { ShortLogo } from '@fluenceur/ui';
import { motion } from 'framer-motion';
import { type ReactNode } from 'react';
import { OnboardingProgress } from './OnboardingProgress';
import type { OnboardingSnapshot } from './onboarding-snapshot';
import type { OnboardingStep } from './onboarding-steps';
import { OnboardingBrandPanel } from './panel/OnboardingBrandPanel';

interface OnboardingLayoutProps {
  children: ReactNode;
  step: OnboardingStep;
  snapshot: OnboardingSnapshot;
  /** Shows "Back" above the form. */
  onBack?: () => void;
  /** The progress bars; hidden on the welcome page and on a page outside the steps. */
  showProgress?: boolean;
}

/**
 * Brand onboarding v2 (mock-up `Brand Onboarding v2`): the form on the left,
 * the brand space building itself on the right. The progress and the enter
 * animation are owned here so the steps only render their form.
 */
export const OnboardingLayout = ({
  children,
  step,
  snapshot,
  onBack,
  showProgress = step !== 'welcome',
}: OnboardingLayoutProps) => (
  <SplitAuthShell
    maxW="452px"
    py={{ base: 6, md: '28px' }}
    brandPanel={<OnboardingBrandPanel step={step} snapshot={snapshot} />}
    topBar={
      <Flex align="center" justify="space-between" gap={4}>
        <ShortLogo height="24px" width="24px" color="ink" />
        <ContactHelpLink whiteSpace="nowrap" _hover={{ textDecoration: 'none', color: 'ink' }} />
      </Flex>
    }
  >
    <motion.div
      key={step}
      initial={{ opacity: 0, y: 7 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
    >
      {showProgress && (
        <OnboardingProgress
          step={step}
          onBack={onBack}
          skipsConnect={snapshot.salesChannel === 'none'}
        />
      )}
      {children}
    </motion.div>
  </SplitAuthShell>
);
